import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import Icon from "../../../components/Icon";
import { hasPermission } from "../../../services/auth";
import { PURCHASE_ORDER_STATUS_KEYS } from "../helpers";
import "./styles.scss";

const HISTORY_PREVIEW = 3;

const StatusPill = ({ status, size = 'regular' }) => (
    <span className={`po-status-pill ${size}`} style={{ '--status-color': status?.color ?? '#7A7499' }}>
        <span className={`dot`} />
        {status?.name ?? '—'}
    </span>
);

const formatDateTime = date => moment(date).format('DD/MM/YYYY [às] HH:mm');

/**
 * Status do pedido de compra: o atual, quando mudou e por quem, a troca por
 * qualquer status cadastrado em Configurações > Status e o histórico.
 */
export const PurchaseOrderStatus = ({ purchase, statuses, canManage, hasInvoices, changing, onChange }) => {
    const [open, setOpen] = useState(false);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const pickerRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) return;

        const closeOnOutsideClick = event => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) setOpen(false);
        };
        const closeOnEscape = event => event.key === 'Escape' && setOpen(false);

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [open]);

    const current = purchase.status;
    const isCanceled = current?.key === PURCHASE_ORDER_STATUS_KEYS.canceled;
    const history = purchase.status_changes ?? [];
    const lastChange = history[0];
    const visibleHistory = showAllHistory ? history : history.slice(0, HISTORY_PREVIEW);

    // Cancelar tem botão próprio, com confirmação: pelo seletor seria fácil
    // demais, e a compra cancelada não volta.
    const options = statuses.filter(status => status.key !== PURCHASE_ORDER_STATUS_KEYS.canceled);

    const blockedReason = status => {
        if (status.key === PURCHASE_ORDER_STATUS_KEYS.invoiced && hasInvoices) {
            return 'Vem das notas de entrada lançadas';
        }
        return null;
    };

    const pick = status => {
        setOpen(false);
        if (status.id !== current?.id) onChange(status);
    };

    return (
        <div className={`po-block po-status ${isCanceled ? 'canceled' : ''}`}>
            <div className={`po-status-head`}>
                <div className={`po-status-current`}>
                    <span className={`po-section-title`}>Status</span>
                    <StatusPill status={current} size={`large`} />
                </div>

                {canManage && !isCanceled &&
                    <div className={`po-status-picker ${open ? 'open' : ''}`} ref={pickerRef}>
                        <button
                            type={`button`}
                            className={`po-status-trigger`}
                            onClick={() => setOpen(value => !value)}
                            disabled={changing}
                            aria-haspopup={`listbox`}
                            aria-expanded={open}
                        >
                            {changing
                                ? <><Icon name={`loading`} size={15} className={`spin`} /> Salvando…</>
                                : <>Alterar status <Icon name={`arrow-dropdown`} size={15} className={`chevron`} /></>
                            }
                        </button>

                        {open &&
                            <div className={`po-status-menu`} role={`listbox`}>
                                {options.map(status => {
                                    const reason = blockedReason(status);
                                    const isCurrent = status.id === current?.id;

                                    return (
                                        <button
                                            key={status.id}
                                            type={`button`}
                                            role={`option`}
                                            aria-selected={isCurrent}
                                            className={`po-status-option ${isCurrent ? 'current' : ''}`}
                                            disabled={!!reason}
                                            title={reason ?? undefined}
                                            onClick={() => pick(status)}
                                        >
                                            <span className={`dot`} style={{ backgroundColor: status.color }} />
                                            <span className={`name`}>{status.name}</span>
                                            {isCurrent && <Icon name={`check`} size={15} className={`check`} />}
                                        </button>
                                    );
                                })}

                                {hasPermission('status') &&
                                    <button
                                        type={`button`}
                                        className={`po-status-manage`}
                                        onClick={() => navigate('/status/list?tab=purchase_order')}
                                    >
                                        <Icon name={`config`} size={14} /> Gerenciar status
                                    </button>
                                }
                            </div>
                        }
                    </div>
                }
            </div>

            <p className={`po-status-meta`}>
                <Icon name={`clock`} size={13} />
                {purchase.status_updated_at
                    ? <>
                        Atualizado em <strong>{formatDateTime(purchase.status_updated_at)}</strong>
                        <span className={`relative`}>({moment(purchase.status_updated_at).fromNow()})</span>
                        {lastChange?.admin && <> por <strong>{lastChange.admin.name}</strong></>}
                    </>
                    : 'Sem data de atualização'
                }
            </p>

            {history.length > 1 &&
                <div className={`po-status-history`}>
                    <span className={`po-status-history-title`}>Histórico</span>
                    <ol>
                        {visibleHistory.map((change, index) => (
                            <li key={change.id} className={index === 0 ? 'latest' : ''}>
                                <span className={`marker`} style={{ '--status-color': change.status?.color ?? '#7A7499' }} />
                                <span className={`name`}>{change.status?.name ?? 'Status excluído'}</span>
                                <span className={`when`}>
                                    {formatDateTime(change.created_at)}
                                    {change.admin && <> · {change.admin.name}</>}
                                </span>
                            </li>
                        ))}
                    </ol>
                    {history.length > HISTORY_PREVIEW &&
                        <button type={`button`} className={`po-status-history-toggle`} onClick={() => setShowAllHistory(value => !value)}>
                            {showAllHistory ? 'Mostrar menos' : `Ver as ${history.length} mudanças`}
                        </button>
                    }
                </div>
            }
        </div>
    );
};

export { StatusPill };
export default PurchaseOrderStatus;
